import createFetchClient from 'openapi-fetch';

import type { paths } from './generated/schema';

export interface ApiClientOptions {
  /** Where the API lives. Same origin in the browser; an absolute URL in tests and tooling. */
  readonly baseUrl: string;
  /**
   * Supplies the bearer token, if there is one. Called per request rather than read once, so a token
   * refreshed mid-session is picked up without rebuilding the client.
   */
  readonly getAccessToken?: () => string | null;
  /** For tests. Defaults to the platform fetch. */
  readonly fetch?: typeof globalThis.fetch;
}

/**
 * The typed client. Every path, parameter and response shape comes from `Docs/api/openapi.json`, which is
 * generated from the API itself and checked for drift in CI (STD-003 §9) — so a frontend call that no
 * longer matches the backend fails to compile rather than failing in someone's hands.
 */
export function createApiClient({ baseUrl, getAccessToken, fetch }: ApiClientOptions) {
  const client = createFetchClient<paths>({
    baseUrl,
    ...(fetch ? { fetch } : {}),
    headers: { 'Content-Type': 'application/json' },
    // The refresh cookie travels with every request (ARCH-003 §2).
    credentials: 'include',
  });

  if (getAccessToken) {
    client.use({
      onRequest({ request }) {
        const token = getAccessToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        return request;
      },
    });
  }

  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * The shape every error comes back in: RFC 7807 ProblemDetails with a `traceId` (STD-003 §4).
 *
 * A caller switches on `type`, which is a stable URI, and never on `title` or `detail` — those are copy,
 * and copy changes.
 */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly instance?: string;
  /** Always present. It is what a support ticket quotes. */
  readonly traceId: string;
  /** Field-level detail on a validation failure. */
  readonly errors?: Readonly<Record<string, readonly string[]>>;
}

/** True when a response body is a ProblemDetails rather than a payload. */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'status' in value &&
    'traceId' in value
  );
}
